import { Link, useMatchRoute } from "@tanstack/react-router";

import { cn } from "@/lib/formatters";
import { LEDDot } from "@/components/ui/LEDDot";

interface NavItem {
  to: string;
  label: string;
  code: string;
  state: "ok" | "warn" | "info" | "off";
  disabled?: boolean;
}

const items: NavItem[] = [
  { to: "/",          label: "Overview",   code: "01", state: "ok"   },
  { to: "/daily",     label: "Daily Tasks", code: "00", state: "ok"  },
  { to: "/approvals", label: "Approvals",  code: "02", state: "info" },
  { to: "/analytics", label: "Analytics",  code: "03", state: "info" },
  { to: "/keywords",  label: "Keywords",   code: "04", state: "info" },
  { to: "/blog",      label: "Blog",       code: "05", state: "info" },
  { to: "/reports",   label: "Reports",    code: "06", state: "info" },
  { to: "/agents",    label: "Agents",     code: "07", state: "info" },
  { to: "/marketing", label: "Marketing",  code: "08", state: "info" },
  { to: "/settings",  label: "Settings",   code: "09", state: "off", disabled: true },
];

export function Sidebar() {
  const matchRoute = useMatchRoute();

  return (
    <aside className="w-60 shrink-0 border-r border-border bg-gradient-to-b from-bg-elev/50 via-bg-elev/25 to-transparent backdrop-blur-md hidden md:flex flex-col relative">
      {/* right edge gradient line */}
      <span className="absolute top-0 bottom-0 right-0 w-px bg-gradient-to-b from-transparent via-violet/30 to-transparent pointer-events-none" />

      <div className="p-5 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="relative flex items-center justify-center w-2.5 h-2.5">
            <span className="absolute inset-0 rounded-full bg-primary animate-pulse-led" />
            <span className="absolute -inset-1 rounded-full bg-primary/20 blur-sm" />
          </span>
          <span className="font-semibold tracking-tight">Thinkler</span>
          <span className="text-muted font-mono text-xs uppercase">console</span>
        </div>
      </div>

      <nav className="flex-1 py-3 space-y-0.5">
        {items.map((item) => {
          const active = !!matchRoute({ to: item.to, fuzzy: true });
          const content = (
            <div
              className={cn(
                "group flex items-center gap-3 px-5 py-2 text-sm transition-all duration-200",
                item.disabled
                  ? "text-muted/40 cursor-not-allowed"
                  : active
                    ? "text-text bg-gradient-to-r from-primary/10 to-transparent border-l-2 border-primary shadow-[inset_0_0_20px_rgba(0,212,255,0.08)]"
                    : "text-muted hover:text-text hover:bg-violet/5 border-l-2 border-transparent hover:border-violet/40",
              )}
            >
              <LEDDot state={active ? "info" : item.state} size={6} pulse={active} />
              <span className="font-mono text-xs text-muted/60 w-6">{item.code}</span>
              <span>{item.label}</span>
            </div>
          );
          return item.disabled ? (
            <div key={item.to}>{content}</div>
          ) : (
            <Link key={item.to} to={item.to}>
              {content}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-border text-xs text-muted font-mono flex items-center justify-between">
        <span>v0.1.0</span>
        <span className="flex items-center gap-1.5">
          <span className="w-1 h-1 rounded-full bg-success animate-pulse-led" />
          <span className="text-success/70">live</span>
        </span>
      </div>
    </aside>
  );
}
